<template>
  <b-modal
    id="create-label-modal"
    ref="create-label-modal"
    dialog-class="modal-1000"
    hide-footer
    centered
    no-enforce-focus
    @hidden="resetModal"
  >
    <template #modal-title>
      <div class="d-flex align-items-center justify-content-between w-100 pr-2">
        <span>{{ $t('Create Order Label') }}</span>
        <b-button
          size="sm"
          variant="outline-primary"
          class="py-25 px-50"
          @click="populateFromOrder(true)"
          :disabled="isLoading"
        >
          <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
          {{ $t('Sync') }}
        </b-button>
      </div>
    </template>
    <b-overlay :show="isLoading" rounded="sm">
      <div :class="{ 'is-last-step': currentStepIndex === 4 }">
        <stepbar
          color="#05a6ce"
          :title="null"
          :subtitle="null"
          shape="circle"
          step-size="sm"
          :finish-button-text="$t('Create Label')"
          :back-button-text="$t('Back')"
          :next-button-text="$t('Continue')"
          @on-complete="handleComplete"
          @on-change="handleStepChange"
          ref="stepbar"
        >
          <!-- Step 1: Receiver & Sender -->
          <tab-content
            :title="$t('Consignee Informations') + ' & ' + $t('Consignor Informations')"
            :before-change="validateStep1"
          >
            <validation-observer ref="step1Form">
              <b-row class="mb-3">
                <!-- Receiver Form -->
                <b-col md="6">
                  <b-card no-body class="border mb-0">
                    <b-card-header class="p-1 border-bottom">
                      <h5 class="mb-0">
                        <feather-icon icon="UserIcon" size="16" class="mr-50" />
                        {{ $t('Consignee Informations') }}
                      </h5>
                    </b-card-header>
                    <b-card-body class="pt-2">
                      <b-row>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignee_name">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Name Surname') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Name Surname')"
                              rules="required"
                              vid="consignee_name"
                            >
                              <b-form-input
                                v-model="consigneeInformations.name"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Name Surname')"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignee_phone_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Phone Number') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Phone Number')"
                              rules="required"
                              vid="consignee_phone_number"
                            >
                              <b-form-input
                                v-model="consigneeInformations.phone_number"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Phone Number')"
                                type="number"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('Country')"
                            rules="required"
                          >
                            <b-form-group
                              label-for="consignee_country"
                              :state="errors.length > 0 ? false : null"
                            >
                              <div class="d-flex form-group-label">
                                <label for="name">{{ $t('Country') }}</label>
                                <span class="text-danger">*</span>
                              </div>
                              <v-select
                                id="consignee_country"
                                v-model="consigneeInformations.country"
                                :options="countries"
                                :placeholder="$t('Make a Choice')"
                                label="text"
                                @input="getConsigneeStateList"
                              />
                              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="6" lg="6">
                          <validation-provider
                            #default="{ errors }"
                            ref="consigneeStateValidation"
                            :name="$t('City')"
                            rules="required"
                            vid="consignee_state"
                          >
                            <b-form-group
                              label-for="consignee_state"
                              :state="errors.length > 0 ? false : null"
                            >
                              <div class="d-flex form-group-label">
                                <label for="name">{{ $t('City') }}</label>
                                <span class="text-danger">*</span>
                              </div>
                              <v-select
                                id="consignee_state"
                                v-model="consigneeInformations.state"
                                :options="consigneeStates"
                                :placeholder="$t('Make a Choice')"
                                label="text"
                                @input="getConsigneeCityList"
                              >
                                <span slot="no-options">{{ $t('No records found') }}</span>
                              </v-select>
                              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="6" lg="6">
                          <validation-provider
                            #default="{ errors }"
                            ref="consigneePostalCodeValidation"
                            :name="$t('Postal Code')"
                            rules="required"
                            vid="consignee_postal_code"
                          >
                            <b-form-group
                              label-for="consignee_postal_code"
                              :state="errors.length > 0 ? false : null"
                            >
                              <div class="d-flex form-group-label">
                                <label for="name">{{ $t('Postal Code') }}</label>
                                <span class="text-danger">*</span>
                              </div>
                              <v-select
                                id="consignee_postal_code"
                                :disabled="!consigneeInformations.country"
                                v-model="consigneeInformations.postal_code"
                                :options="consigneePostalCodeOptions"
                                :placeholder="$t('Enter Postal Code')"
                                label="combinedLabel"
                                @search="handleConsigneePostalCodeSearch"
                                taggable
                                push-tags
                              >
                                <span slot="no-options">{{ $t('No records found') }}</span>
                              </v-select>
                              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="attention_email">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('E-Mail') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('E-Mail')"
                              rules="required|email"
                              vid="attention_email"
                            >
                              <b-form-input
                                v-model="consigneeInformations.attention_email"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('E-Mail')"
                                type="email"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignee_tax_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Tax Number (optional)') }}</label>
                            </div>
                            <b-form-input
                              v-model="consigneeInformations.tax_number"
                              :placeholder="$t('Tax Number (optional)')"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignee_eori_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('EORI Number (optional)') }}</label>
                            </div>
                            <b-form-input
                              v-model="consigneeInformations.eori_number"
                              :placeholder="$t('EORI Number (optional)')"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" lg="12">
                          <b-form-group label-for="consignee_address">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Address') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Address')"
                              rules="required"
                              vid="consignee_address"
                            >
                              <b-form-textarea
                                v-model="consigneeInformations.address"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Address')"
                                rows="3"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                </b-col>

                <!-- Sender Form -->
                <b-col md="6">
                  <b-card no-body class="border mb-0">
                    <b-card-header class="p-1 border-bottom">
                      <h5 class="mb-0">
                        <feather-icon icon="BriefcaseIcon" size="16" class="mr-50" />
                        {{ $t('Consignor Informations') }}
                      </h5>
                    </b-card-header>
                    <b-card-body class="pt-2">
                      <b-row>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_attention_name">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Attention Name') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Attention Name')"
                              rules="required"
                              vid="consignor_attention_name"
                            >
                              <b-form-input
                                v-model="consignorInformations.attention_name"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Attention Name')"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_name">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Company Name') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Company Name')"
                              rules="required"
                              vid="consignor_name"
                            >
                              <b-form-input
                                v-model="consignorInformations.name"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Company Name')"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_phone_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Phone Number') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Phone Number')"
                              rules="required"
                              vid="consignor_phone_number"
                            >
                              <b-form-input
                                v-model="consignorInformations.phone_number"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Phone Number')"
                                type="number"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_attention_email">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('E-Mail') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('E-Mail')"
                              rules="required|email"
                              vid="consignor_attention_email"
                            >
                              <b-form-input
                                v-model="consignorInformations.attention_email"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('E-Mail')"
                                type="email"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('Country')"
                            rules="required"
                          >
                            <b-form-group
                              label-for="consignor_country"
                              :state="errors.length > 0 ? false : null"
                            >
                              <div class="d-flex form-group-label">
                                <label for="name">{{ $t('Country') }}</label>
                                <span class="text-danger">*</span>
                              </div>
                              <v-select
                                id="consignor_country"
                                v-model="consignorInformations.country"
                                :options="countries"
                                :placeholder="$t('Make a Choice')"
                                label="text"
                                @input="getConsignorStateList"
                              />
                              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="6" lg="6">
                          <validation-provider
                            #default="{ errors }"
                            ref="consignorStateValidation"
                            :name="$t('City')"
                            rules="required"
                            vid="consignor_state"
                          >
                            <b-form-group
                              label-for="consignor_state"
                              :state="errors.length > 0 ? false : null"
                            >
                              <div class="d-flex form-group-label">
                                <label for="name">{{ $t('City') }}</label>
                                <span class="text-danger">*</span>
                              </div>
                              <v-select
                                id="consignor_state"
                                v-model="consignorInformations.state"
                                :options="consignorStates"
                                :placeholder="$t('Make a Choice')"
                                label="text"
                                @input="getConsignorCityList"
                              >
                                <span slot="no-options">{{ $t('No records found') }}</span>
                              </v-select>
                              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="6" lg="6">
                          <validation-provider
                            #default="{ errors }"
                            ref="consignorPostalCodeValidation"
                            :name="$t('Postal Code')"
                            rules="required"
                            vid="consignor_postal_code"
                          >
                            <b-form-group
                              label-for="consignor_postal_code"
                              :state="errors.length > 0 ? false : null"
                            >
                              <div class="d-flex form-group-label">
                                <label for="name">{{ $t('Postal Code') }}</label>
                                <span class="text-danger">*</span>
                              </div>
                              <v-select
                                id="consignor_postal_code"
                                :disabled="!consignorInformations.country"
                                v-model="consignorInformations.postal_code"
                                :options="postalCodeOptions"
                                :placeholder="$t('Enter Postal Code')"
                                label="combinedLabel"
                                @search="handlePostalCodeSearch"
                                taggable
                                push-tags
                              >
                                <span slot="no-options">{{ $t('No records found') }}</span>
                              </v-select>
                              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_tax_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Tax Number (optional)') }}</label>
                            </div>
                            <b-form-input
                              v-model="consignorInformations.tax_number"
                              :placeholder="$t('Tax Number (optional)')"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_eori_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('EORI Number (optional)') }}</label>
                            </div>
                            <b-form-input
                              v-model="consignorInformations.eori_number"
                              :placeholder="$t('EORI Number (optional)')"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="6" lg="6">
                          <b-form-group label-for="consignor_ioss_number">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('IOSS Number (optional)') }}</label>
                            </div>
                            <b-form-input
                              v-model="consignorInformations.ioss_number"
                              :placeholder="$t('IOSS Number (optional)')"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" lg="12">
                          <b-form-group label-for="consignor_address">
                            <div class="d-flex form-group-label">
                              <label for="name">{{ $t('Address') }}</label>
                              <span class="text-danger">*</span>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              :name="$t('Address')"
                              rules="required"
                              vid="consignor_address"
                            >
                              <b-form-textarea
                                v-model="consignorInformations.address"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="$t('Address')"
                                rows="3"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </validation-observer>
          </tab-content>

          <!-- Step 2: Package & Product Info -->
          <tab-content :title="$t('Package & Item Information')" :before-change="validateStep2">
            <validation-observer ref="step2Form" tag="form">
              <b-row class="mb-2">
                <b-col cols="12">
                  <h5 class="mb-0">📦 {{ $t('Package & Item Details') }}</h5>
                  <small class="text-muted">{{
                    $t('The following items were found and displayed by the system.')
                  }}</small>
                </b-col>
              </b-row>

              <div
                v-for="(pkg, pkgIndex) in defaultPackageValues"
                :key="pkg.id || pkgIndex"
                class="mb-2"
              >
                <b-card no-body class="border">
                  <b-card-body>
                    <h5 class="mb-1 font-weight-bolder">
                      {{ $t('Package') }} {{ pkgIndex + 1 }} — {{ $t('All Items') }}
                    </h5>

                    <div class="mb-1" v-if="pkg.items && pkg.items.length">
                      <b-form-checkbox
                        v-for="(item, idx) in pkg.items"
                        :key="item.id || idx"
                        :checked="true"
                        disabled
                        class="mb-50"
                      >
                        {{ item.description }} × {{ item.quantity }}
                      </b-form-checkbox>
                    </div>

                    <p class="text-muted font-small-3 mb-1">
                      {{ pkg.items ? pkg.items.length : 0 }} {{ $t('items') }} — 1
                      {{ $t('Package') }}
                    </p>

                    <b-row>
                      <b-col md="12" lg="3">
                        <validation-provider
                          #default="{ errors }"
                          :name="$t('Weight (kg)')"
                          :vid="`weight_${pkgIndex}`"
                          rules="required|doubleOrInteger"
                        >
                          <b-form-group :label="$t('Weight (kg)')">
                            <template #label>
                              <span
                                >{{ $t('Weight (kg)') }} <span class="text-danger">*</span></span
                              >
                            </template>
                            <b-form-input
                              v-model="pkg.weight"
                              :state="errors.length > 0 ? false : null"
                              type="number"
                              placeholder="0.00"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </b-form-group>
                        </validation-provider>
                      </b-col>

                      <b-col md="12" lg="9">
                        <b-form-group>
                          <template #label>{{ $t('Dimensions') }}</template>
                          <b-row>
                            <b-col cols="4">
                              <validation-provider
                                #default="{ errors }"
                                :name="$t('Width')"
                                :vid="`width_${pkgIndex}`"
                                rules="required|integer"
                              >
                                <b-form-input
                                  v-model="pkg.width"
                                  :state="errors.length > 0 ? false : null"
                                  type="number"
                                  :placeholder="$t('Width')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                              </validation-provider>
                            </b-col>
                            <b-col cols="4">
                              <validation-provider
                                #default="{ errors }"
                                :name="$t('Length')"
                                :vid="`length_${pkgIndex}`"
                                rules="required|integer"
                              >
                                <b-form-input
                                  v-model="pkg.length"
                                  :state="errors.length > 0 ? false : null"
                                  type="number"
                                  :placeholder="$t('Length')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                              </validation-provider>
                            </b-col>
                            <b-col cols="4">
                              <validation-provider
                                #default="{ errors }"
                                :name="$t('Height')"
                                :vid="`height_${pkgIndex}`"
                                rules="required|integer"
                              >
                                <b-form-input
                                  v-model="pkg.height"
                                  :state="errors.length > 0 ? false : null"
                                  type="number"
                                  :placeholder="$t('Height')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                              </validation-provider>
                            </b-col>
                          </b-row>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-card-body>
                </b-card>
              </div>

              <!-- Ürün Detayları -->
              <div class="mt-2" v-if="allOrderProducts.length > 0">
                <h5 class="mb-1 font-weight-bolder">🏷️ {{ $t('Product Details') }}</h5>
                <b-card no-body class="border">
                  <b-card-body>
                    <div v-for="(item, idx) in allOrderProducts" :key="item.id || idx" class="mb-2">
                      <b-row>
                        <b-col md="4" lg="4">
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('Product Name')"
                            :vid="`prod_desc_${idx}`"
                            rules="required"
                          >
                            <b-form-group>
                              <template #label
                                >{{ $t('Product Name') }}
                                <span class="text-danger">*</span></template
                              >
                              <b-form-textarea
                                v-model="item.description"
                                :state="errors.length > 0 ? false : null"
                                rows="2"
                                no-resize
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="3" lg="3">
                          <b-form-group :label="$t('Currency')">
                            <v-select
                              v-model="item.currency"
                              :options="currencies"
                              :reduce="val => val.code"
                              label="code"
                              :clearable="false"
                            >
                              <template #option="option">
                                <span>{{ option.text }}</span>
                              </template>
                            </v-select>
                          </b-form-group>
                        </b-col>
                        <b-col md="2" lg="2">
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('Quantity')"
                            :vid="`prod_qty_${idx}`"
                            rules="required|integer"
                          >
                            <b-form-group :label="$t('Quantity')">
                              <b-form-input
                                v-model="item.quantity"
                                :state="errors.length > 0 ? false : null"
                                type="number"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="3" lg="3">
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('Unit Price')"
                            :vid="`prod_price_${idx}`"
                            rules="required|doubleOrInteger"
                          >
                            <b-form-group :label="$t('Unit Price')">
                              <b-form-input
                                v-model="item.unit_price"
                                :state="errors.length > 0 ? false : null"
                                type="number"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col md="8" lg="8">
                          <b-form-group :label="$t('Gtip Code')">
                            <SelectBox
                              :id="`gtip_${idx}`"
                              name="gtip"
                              url="api/v1/gtip/search"
                              returned-key="code"
                              :value="item.gtipSelect"
                              :placeholder="$t('Make a Choice')"
                              :min="4"
                              wrap-text
                              @setValue="setGtipValue(idx, $event)"
                            />
                            <input v-model="item.gtip" type="hidden" />
                          </b-form-group>
                        </b-col>
                        <b-col md="4" lg="4">
                          <b-form-group :label="$t('Origin')">
                            <v-select
                              v-model="item.origin"
                              :options="['TR', 'US', 'CN', 'GB', 'DE']"
                              :placeholder="$t('Make a Choice')"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                      <hr v-if="idx !== allOrderProducts.length - 1" class="my-2" />
                    </div>
                  </b-card-body>
                </b-card>
              </div>
            </validation-observer>
          </tab-content>

          <!-- Step 3: Shipment Info -->
          <tab-content :title="$t('Shipment')" :before-change="validateStep3">
            <b-row>
              <b-col md="8" offset-md="2">
                <b-card no-body class="border p-2">
                  <b-form-group label-for="incoterms">
                    <div class="d-flex form-group-label">
                      <label for="name">{{ $t('Incoterms Type') }}</label>
                    </div>
                    <v-select
                      id="incoterms"
                      v-model="shipmentInfo.incoterms"
                      :options="['DAP', 'DDP', 'DDU']"
                      :placeholder="$t('Make a Choice')"
                    />
                  </b-form-group>

                  <div class="border rounded p-2 mt-2">
                    <b-form-checkbox
                      v-model="shipmentInfo.postType"
                      :value="2"
                      :unchecked-value="null"
                    >
                      {{ $t('Declare as Micro Export') }}
                      <div class="text-muted" style="font-size: 11px">
                        {{ $t('Micro Export Subtitle') }}
                      </div>
                    </b-form-checkbox>

                    <b-alert
                      :show="shipmentInfo.postType === 2"
                      variant="warning"
                      class="mt-2 mb-0 p-1"
                    >
                      <div class="alert-body">
                        <feather-icon icon="InfoIcon" class="mr-50" />
                        <small>{{ $t('Micro Export Alert') }}</small>
                      </div>
                    </b-alert>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </tab-content>

          <!-- Step 4: Documents -->
          <tab-content :title="$t('Document')" :before-change="validateStep4">
            <b-row>
              <b-col md="8" offset-md="2">
                <b-card no-body class="p-2">
                  <b-form-checkbox v-model="documents.autoProforma" class="mb-1">
                    {{ $t('Automatically generate proforma') }}
                    <small class="d-block text-muted">{{
                      $t(
                        'A proforma invoice will be automatically generated based on the order details.'
                      )
                    }}</small>
                  </b-form-checkbox>

                  <hr />

                  <b-form-group label-for="invoice_number">
                    <div class="d-flex form-group-label">
                      <label for="name">{{ $t('Invoice Number (optional)') }}</label>
                    </div>
                    <b-form-input
                      id="invoice_number"
                      :placeholder="$t('Invoice Number (optional)')"
                      v-model="documents.documentNumber"
                    />
                  </b-form-group>

                  <b-form-group label-for="upload_file">
                    <div class="d-flex form-group-label">
                      <label for="name">{{ $t('Upload File (PDF, JPG, PNG - Max 5MB)') }}</label>
                    </div>
                    <b-form-file
                      id="upload_file"
                      v-model="documents.file"
                      :placeholder="$t('Choose a file or drop it here')"
                      :drop-placeholder="$t('Drop file here')"
                      :browse-text="$t('Browse')"
                      @input="handleFileUpload"
                      accept=".pdf, .jpg, .png"
                    />
                  </b-form-group>
                </b-card>
              </b-col>
            </b-row>
          </tab-content>

          <!-- Step 5: Offer & Label -->
          <tab-content :title="$t('Offers')">
            <div class="offers-container card border shadow-none mb-0">
              <!-- Top bar / Header -->
              <div
                class="card-header bg-light py-1 px-2 border-bottom d-flex align-items-center font-small-3"
                style="border-top-left-radius: 8px; border-top-right-radius: 8px"
              >
                <div class="mr-2">
                  <span class="text-muted">{{ $t('Weight') }}:</span>
                  <span class="font-weight-bold ml-25"
                    >{{ packageDetails.weight || '—' }} {{ $t('kg') }}</span
                  >
                </div>
                <div>
                  <span class="text-muted">{{ $t('Dimensions') }}:</span>
                  <span class="font-weight-bold ml-25">
                    {{ packageDetails.width || '—' }}×{{ packageDetails.length || '—' }}×{{
                      packageDetails.height || '—'
                    }}
                    {{ $t('cm') }}
                  </span>
                </div>
              </div>

              <div class="card-body p-2">
                <div v-if="isLoadingPrices" class="text-center py-5">
                  <b-spinner variant="primary" />
                  <div class="mt-1 text-muted">{{ $t('Calculating prices...') }}</div>
                </div>

                <div v-else>
                  <div v-if="providers.length > 0">
                    <div class="text-muted mb-2 font-small-3">
                      {{ $t('The cheapest option was automatically selected.') }}
                    </div>

                    <!-- Offers List -->
                    <div class="offers-list mb-2">
                      <div
                        v-for="item in providers"
                        :key="item.id"
                        class="offer-item d-flex align-items-center justify-content-between p-2 mb-1 rounded border cursor-pointer"
                        :class="{
                          'offer-item-selected border-primary': item.id === selectedProviderId
                        }"
                        @click="selectedProviderId = item.id"
                      >
                        <div class="d-flex align-items-center">
                          <!-- Custom Radio Button -->
                          <div
                            class="custom-radio-circle mr-1 d-flex align-items-center justify-content-center"
                          >
                            <div
                              v-if="item.id === selectedProviderId"
                              class="custom-radio-inner"
                            ></div>
                          </div>

                          <div>
                            <div class="d-flex align-items-center">
                              <span class="font-weight-bold text-dark h5 mb-0">{{
                                item.name || item.provider_name
                              }}</span>
                              <span
                                v-if="item.id === cheapestProviderId"
                                class="badge badge-light-success ml-50 font-small-2 px-50 py-25"
                              >
                                {{ $t('Cheapest') }}
                              </span>
                            </div>
                            <small class="text-muted mt-25 d-block">
                              {{ item.delivery_time || '3-5' }} {{ $t('business days') }} &middot;
                              {{ $t('Estimated') }}:
                              {{ getEstimatedDeliveryDate(item.delivery_time) }}
                            </small>
                          </div>
                        </div>

                        <div class="text-primary font-weight-bolder h4 mb-0">
                          {{ formatPrice(item.total_price || item.price, item.currency) }}
                        </div>
                      </div>
                    </div>

                    <!-- Selected Summary Badge -->
                    <div
                      v-if="selectedProvider"
                      class="selected-summary-box alert alert-success p-1 d-flex align-items-center justify-content-center text-success font-weight-bold mb-2"
                    >
                      <span>
                        {{ selectedProvider.name || selectedProvider.provider_name }} &middot;
                        {{
                          formatPrice(
                            selectedProvider.total_price || selectedProvider.price,
                            selectedProvider.currency
                          )
                        }}
                        &middot;
                        {{ selectedProvider.delivery_time || '3-5' }} {{ $t('business days') }}
                      </span>
                    </div>

                    <!-- Action Button -->
                    <b-button
                      variant="success"
                      block
                      size="lg"
                      class="create-label-btn py-1 font-weight-bold"
                      @click="handleComplete"
                    >
                      <span class="mr-50">✓</span> {{ $t('Create Label') }}
                    </b-button>
                  </div>
                  <div v-else class="text-center py-5 text-muted">
                    {{ $t('No shipping options found for the specified criteria.') }}
                  </div>
                </div>
              </div>
            </div>
          </tab-content>
        </stepbar>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormTextarea,
  BFormCheckbox,
  BAlert,
  BFormFile,
  BSpinner,
  BOverlay,
  BButton
} from 'bootstrap-vue';
import { TabContent } from 'vue-form-wizard';
import Stepbar from '@/components/Stepbar.vue';
import vSelect from 'vue-select';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { mapGetters } from 'vuex';
import useService from '@/auth/service/useService';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import SelectBox from '@/components/SelectBox.vue';
import { helper } from '@core/mixins/helper.js';
import valuableCurrencies from '@/constants/currencies.json';

export default {
  name: 'CreateLabelModal',
  components: {
    BModal,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormTextarea,
    BFormCheckbox,
    BAlert,
    BFormFile,
    BSpinner,
    BOverlay,
    BButton,
    Stepbar,
    TabContent,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    SelectBox
  },
  mixins: [helper],
  props: {
    order: {
      type: Object,
      default: () => null
    }
  },
  watch: {},
  data() {
    return {
      isLoading: false,
      isLoadingPrices: false,
      consignorInformations: {
        country: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: '',
        tax_number: '',
        eori_number: '',
        ioss_number: ''
      },
      consigneeInformations: {
        country: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: '',
        tax_number: '',
        eori_number: ''
      },
      consignorAddressBook: null,
      consigneeAddressBook: null,

      totalPackageRow: 1,
      defaultPackageValues: [],
      postalCodes: [],
      consigneePostalCodes: [],
      minimumPostalCodeLength: 3,
      filteredPostalCodes: [],
      consigneeFilteredPostalCodes: [],
      shipmentInfo: {
        incoterms: 'DAP',
        postType: null
      },
      documents: {
        autoProforma: true,
        documentNumber: '',
        file: null,
        uploadedFile: null,
        uploadedDocumentId: null
      },
      providers: [],
      selectedProviderId: null,
      currentStepIndex: 0,
      referenceNo: null
    };
  },
  computed: {
    cheapestProviderId() {
      return this.providers.length > 0 ? this.providers[0].id : null;
    },
    selectedProvider() {
      return this.providers.find(p => p.id === this.selectedProviderId) || null;
    },
    ...mapGetters({
      countries: 'app/countries',
      consignorStates: 'app/consignorStates',
      consignorCities: 'app/consignorCities',
      consigneeStates: 'app/consigneeStates',
      consigneeCities: 'app/consigneeCities',
      preferences: 'app/preferences'
    }),
    currencies() {
      return valuableCurrencies.map(item => ({
        text: `${this.$t(item.name)} (${item.code_text})`,
        code: item.code
      }));
    },
    postalCodeOptions() {
      return this.mapToOptions(this.filteredPostalCodes);
    },
    consigneePostalCodeOptions() {
      return this.mapToOptions(this.consigneeFilteredPostalCodes);
    },
    calculatedDesi() {
      const w = this.packageDetails.width || 0;
      const l = this.packageDetails.length || 0;
      const h = this.packageDetails.height || 0;
      return Math.ceil((w * l * h) / 5000);
    },
    packageDetails() {
      if (this.defaultPackageValues && this.defaultPackageValues.length > 0) {
        return this.defaultPackageValues[0];
      }
      return { weight: 0, width: 0, length: 0, height: 0 };
    },
    allOrderProducts() {
      if (!this.defaultPackageValues) return [];
      return this.defaultPackageValues.reduce((acc, pkg) => {
        if (pkg.items && Array.isArray(pkg.items)) {
          acc.push(...pkg.items);
        }
        return acc;
      }, []);
    }
  },
  methods: {
    mapToOptions(list) {
      return list.map(code => ({
        value: code.zipcode,
        combinedLabel: `${code.zipcode} - ${code.place}`,
        state: code.state,
        city: code.city
      }));
    },
    setGtipValue(idx, data) {
      const item = this.allOrderProducts[idx];
      if (item) {
        item.gtip = data?.id || '';
        item.gtipSelect = data || null;
      }
    },

    async setConsignorAddressData(data) {
      if (data) {
        await this.setCurrentAddressData(data, 'consignorInformations', 'consignor');
      }
    },
    async setConsigneeAddressData(data) {
      if (data) {
        await this.setCurrentAddressData(data, 'consigneeInformations', 'consignee');
      }
    },
    show() {
      this.populateFromOrder();
      this.resetModal();
      this.$refs['create-label-modal'].show();
    },
    hide() {
      this.$refs['create-label-modal'].hide();
    },
    resetModal() {
      this.providers = [];
      this.selectedProviderId = null;
      this.documents.file = null;
      this.consignorAddressBook = null;
      this.consigneeAddressBook = null;
      this.referenceNo = null;
      if (this.$refs.step1Form) this.$refs.step1Form.reset();
      if (this.$refs.step2Form) this.$refs.step2Form.reset();
    },
    async populateFromOrder(sync = false) {
      if (!this.order) return;
      this.isLoading = true;
      try {
        const orderId = this.order.raw
          ? this.order.raw.id || this.order.raw.order_id
          : this.order.id || this.order.order_id;

        // If not syncing, try to fill from this.order.raw first
        if (
          !sync &&
          this.order.raw &&
          (this.order.raw.receiver || this.order.raw.seller || this.order.raw.details)
        ) {
          const rawData =
            this.order.raw.details &&
            (this.order.raw.details.receiver ||
              this.order.raw.details.seller ||
              this.order.raw.details.products)
              ? { ...this.order.raw.details, id: this.order.raw.id }
              : this.order.raw;
          await this.fillForm(rawData);
        }

        const params = sync ? { sync: true } : {};
        const response = await useService.getCoupangOrderConsignmentData(orderId, params);
        if (response && response.data) {
          if (response.data.id) {
            this.referenceNo = response.data.id;
          }
          // Backend may return nested structure where receiver/seller/products live under `details`
          const formData =
            response.data.details &&
            (response.data.details.receiver ||
              response.data.details.seller ||
              response.data.details.products)
              ? { ...response.data.details, id: response.data.id }
              : response.data;
          await this.fillForm(formData);
        }
      } catch (error) {
        this.showErrorMessage(this.$t('Default Error Message'));
      } finally {
        this.isLoading = false;
      }
    },
    async fillForm(data) {
      if (!data) return;

      // Process receiver data as consignee
      if (data.receiver) {
        this.consigneeInformations.name = data.receiver.name || '';
        this.consigneeInformations.phone_number = data.receiver.safe_number || '';
        this.consigneeInformations.country = '';
        this.consigneeInformations.state = '';
        this.consigneeInformations.attention_email =
          data.orderer && data.orderer.email ? data.orderer.email : '';
        this.consigneeInformations.tax_number = '';
        this.consigneeInformations.eori_number = '';

        const line1 = data.receiver.line1 || '';
        const line2 = data.receiver.line2 || '';
        if (line1 && line2) {
          this.consigneeInformations.address = `${line1} ${line2}`;
        } else if (line1) {
          this.consigneeInformations.address = line1;
        } else {
          this.consigneeInformations.address = '';
        }

        if (data.receiver.post_code) {
          const postCodeStr = String(data.receiver.post_code).trim();

          // Coupang is predominantly South Korea.
          const matchedCountry = this.countries.find(c => c.iso2 === 'KR');
          if (matchedCountry) {
            this.consigneeInformations.country = matchedCountry;

            // Temporarily disable isLoading so getConsigneeStateList can run
            const wasLoading = this.isLoading;
            this.isLoading = false;

            // This clears the postal_code in the form, so we must set it AFTER
            await this.getConsigneeStateList(matchedCountry);

            this.isLoading = wasLoading;
          }

          this.consigneeInformations.postal_code = {
            label: postCodeStr,
            value: postCodeStr,
            combinedLabel: postCodeStr
          };

          // Try to resolve state from postal code
          useService
            .autoPostalCodes({ zipcode: postCodeStr, country_code: 'KR' })
            .then(response => {
              if (response && response.data && response.data.length > 0) {
                const match = response.data[0];
                const searchStr1 = (match.state || '').toLowerCase();
                const searchStr2 = (match.province || '').toLowerCase();
                const searchStr3 = (match.place || '').toLowerCase();

                if ((searchStr1 || searchStr2 || searchStr3) && this.consigneeStates) {
                  const matchedState = this.consigneeStates.find(s => {
                    const text = (s.text || '').toLowerCase();
                    const value = String(s.value || '').toLowerCase();
                    return (
                      (searchStr1 &&
                        (text === searchStr1 ||
                          value === searchStr1 ||
                          text.includes(searchStr1) ||
                          searchStr1.includes(text))) ||
                      (searchStr2 &&
                        (text === searchStr2 ||
                          value === searchStr2 ||
                          text.includes(searchStr2) ||
                          searchStr2.includes(text))) ||
                      (searchStr3 &&
                        (text === searchStr3 ||
                          value === searchStr3 ||
                          text.includes(searchStr3) ||
                          searchStr3.includes(text)))
                    );
                  });
                  if (matchedState) {
                    this.consigneeInformations.state = matchedState;
                  }
                }
              }
            });
        } else {
          this.consigneeInformations.postal_code = '';
        }
      }

      // Process seller data as consignor
      if (data.seller) {
        this.consignorInformations.attention_name = '';
        this.consignorInformations.name = data.seller.seller_name || '';
        this.consignorInformations.phone_number = '';
        this.consignorInformations.attention_email = '';
        this.consignorInformations.country = '';
        this.consignorInformations.state = '';
        this.consignorInformations.postal_code = '';
        this.consignorInformations.address = '';
        this.consignorInformations.tax_number = '';
        this.consignorInformations.eori_number = '';
        this.consignorInformations.ioss_number = '';
      }

      // Process products as packages
      if (data.products && data.products.length > 0) {
        const items = data.products.map(product => ({
          description: product.vendor_item_name || product.name || '',
          quantity: product.shipping_count || product.quantity || 1,
          currency:
            product.sales_price && product.sales_price.currency
              ? product.sales_price.currency
              : product.currency || '',
          unit_price:
            product.sales_price && product.sales_price.amount !== undefined
              ? product.sales_price.amount
              : product.unit_price || '',
          origin: '',
          gtip: '',
          gtipSelect: null
        }));

        this.defaultPackageValues = [
          {
            weight: '',
            width: '',
            length: '',
            height: '',
            items
          }
        ];
        this.totalPackageRow = 1;
      }

      // Step 3 Shipment Info
      this.shipmentInfo.incoterms = '';
      this.shipmentInfo.postType = null;
    },
    async createLabel() {
      // Deprecated, createConsignment in handleComplete is used instead
    },
    async setCurrentAddressData(data, dataKey, prefix) {
      if (!dataKey) return;

      if (data.countryIso2) {
        this[dataKey].country = this.countries.find(
          x => x.iso2 === data.countryIso2 || x.value === data.countryIso2
        );
      }

      if (this[dataKey].country && data.state_code) {
        await this.$store.dispatch(
          `app/get${prefix === 'consignor' ? 'Consignor' : 'Consignee'}States`,
          this[dataKey].country.value
        );
        const states = prefix === 'consignor' ? this.consignorStates : this.consigneeStates;
        this[dataKey].state = states.find(
          x => x.stateCode === data.state_code || x.text === data.state
        ) || { text: data.state, value: data.state_code, stateCode: data.state_code };
      }

      if (this[dataKey].state && this[dataKey].state.value && data.city) {
        await this.$store.dispatch(
          `app/get${prefix === 'consignor' ? 'Consignor' : 'Consignee'}Cities`,
          {
            countryId: this[dataKey].country.value,
            cityId: this[dataKey].state.value
          }
        );
        const cities = prefix === 'consignor' ? this.consignorCities : this.consigneeCities;
        this[dataKey].city = cities.find(x => x.text === data.city) || {
          text: data.city,
          value: data.city
        };
      }

      if (data.postal_code) {
        this[dataKey].postal_code = {
          label: data.postal_code,
          value: data.postal_code,
          combinedLabel: `${data.postal_code}`
        };
      }

      if (data.attention_name) {
        this[dataKey].attention_name = data.attention_name;
      }
      if (data.name) {
        this[dataKey].name = data.name;
      }
      if (data.phone) {
        this[dataKey].phone_number = data.phone;
      }
      if (data.email) {
        this[dataKey].attention_email = data.email;
      }
      if (data.address) {
        this[dataKey].address = data.address;
      }
      if (data.tax_number) {
        this[dataKey].tax_number = data.tax_number;
      }
    },
    getConsignorStateList(data) {
      if (this.isLoading) return;
      this.consignorInformations.state = '';
      if (this.$refs.consignorStateValidation) this.$refs.consignorStateValidation.reset();
      this.consignorInformations.postal_code = '';
      if (this.$refs.consignorPostalCodeValidation)
        this.$refs.consignorPostalCodeValidation.reset();
      this.postalCodes = [];
      this.filteredPostalCodes = [];
      this.getConsignorCityList('');
      if (data && data.value) {
        this.$store.dispatch('app/getConsignorStates', data.value);
      }
    },
    getConsignorCityList(data) {
      if (this.isLoading) return;
      this.consignorInformations.city = '';
      if (this.$refs.consignorCityValidation) this.$refs.consignorCityValidation.reset();
      if (data && data.value && this.consignorInformations.country) {
        this.$store.dispatch('app/getConsignorCities', {
          countryId: this.consignorInformations.country.value,
          cityId: data.value
        });
      }
    },
    getConsigneeStateList(data) {
      if (this.isLoading) return Promise.resolve();
      this.consigneeInformations.state = '';
      if (this.$refs.consigneeStateValidation) this.$refs.consigneeStateValidation.reset();
      this.consigneeInformations.postal_code = '';
      if (this.$refs.consigneePostalCodeValidation)
        this.$refs.consigneePostalCodeValidation.reset();
      this.consigneePostalCodes = [];
      this.consigneeFilteredPostalCodes = [];
      this.getConsigneeCityList('');
      if (data && data.value) {
        return this.$store.dispatch('app/getConsigneeStates', data.value);
      }
      return Promise.resolve();
    },
    getConsigneeCityList(data) {
      if (this.isLoading) return;
      this.consigneeInformations.city = '';
      if (this.$refs.consigneeCityValidation) this.$refs.consigneeCityValidation.reset();
      if (data && data.value && this.consigneeInformations.country) {
        this.$store.dispatch('app/getConsigneeCities', {
          countryId: this.consigneeInformations.country.value,
          cityId: data.value
        });
      }
    },
    handlePostalCodeSearch(query) {
      if (query.length >= this.minimumPostalCodeLength && this.consignorInformations.country) {
        const payload = {
          country_code:
            this.consignorInformations.country.iso2 || this.consignorInformations.country.value,
          zipcode: query
        };
        useService.autoPostalCodes(payload).then(response => {
          this.filteredPostalCodes = response.data;
        });
      }
    },
    handleConsigneePostalCodeSearch(query) {
      if (query.length >= this.minimumPostalCodeLength && this.consigneeInformations.country) {
        const payload = {
          country_code:
            this.consigneeInformations.country.iso2 || this.consigneeInformations.country.value,
          zipcode: query
        };
        useService.autoPostalCodes(payload).then(response => {
          this.consigneeFilteredPostalCodes = response.data;
        });
      }
    },
    deletePackage() {
      if (this.totalPackageRow > 1) {
        this.totalPackageRow--;
      }
    },
    addNewPackage() {
      this.validateStep2().then(valid => {
        if (valid) {
          this.totalPackageRow++;
        }
      });
    },
    packageValidationFunction() {
      return this.$refs.step2Form.validate();
    },
    async validateStep1() {
      return this.$refs.step1Form.validate();
    },
    async validateStep2() {
      return new Promise((resolve, reject) => {
        this.packageValidationFunction().then(valid => {
          if (valid) {
            let hasItems = true;
            for (let i = 0; i < this.defaultPackageValues.length; i++) {
              if (
                !this.defaultPackageValues[i].items ||
                !this.defaultPackageValues[i].items.length
              ) {
                hasItems = false;
              }
            }
            if (!hasItems) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('Package Content Warning'),
                  icon: 'AlertTriangleIcon',
                  variant: 'warning'
                }
              });
              reject();
            } else {
              resolve(true);
            }
          } else {
            reject();
          }
        });
      });
    },
    async validateStep3() {
      return true;
    },
    async handleFileUpload(file) {
      if (file) {
        if (file !== this.documents.uploadedFile) {
          this.isLoading = true;
          try {
            const formData = new FormData();
            formData.append('document_file', file);
            formData.append('document_type', '13'); // Proforma invoice type
            formData.append('document_issued_at', new Date().toISOString().split('T')[0]);
            if (this.documents.documentNumber) {
              formData.append('document_reference', this.documents.documentNumber);
            }

            const docRes = await useService.uploadConsignmentDocument(formData);
            this.documents.uploadedDocumentId = docRes?.data?.id || docRes?.id;
            this.documents.uploadedFile = file;
          } catch (error) {
            this.showErrorMessage(
              error.message || error.detail || error,
              this.$t('Error uploading document')
            );
            this.documents.file = null;
          } finally {
            this.isLoading = false;
          }
        }
      } else {
        this.documents.uploadedDocumentId = null;
        this.documents.uploadedFile = null;
      }
    },
    async validateStep4() {
      if (this.documents.file && !this.documents.uploadedDocumentId) {
        await this.handleFileUpload(this.documents.file);
        if (!this.documents.uploadedDocumentId) return false;
      }
      return true;
    },
    async handleStepChange(prevIndex, nextIndex) {
      this.currentStepIndex = nextIndex;
      if (nextIndex === 4) {
        // Step 5 is index 4
        this.fetchPrices();
      }
    },
    async fetchPrices() {
      this.isLoadingPrices = true;
      this.providers = [];
      this.selectedProviderId = null;

      try {
        const packages = this.defaultPackageValues.map(pkg => ({
          weight: pkg.weight ? parseFloat(pkg.weight) : 0,
          width: pkg.width ? parseFloat(pkg.width) : 0,
          length: pkg.length ? parseFloat(pkg.length) : 0,
          height: pkg.height ? parseFloat(pkg.height) : 0,
          package_type: 1
        }));

        const postType = this.shipmentInfo.postType;
        const packagesWithPostType = packages.map(pkg => ({
          ...pkg,
          post_type: postType || 4
        }));

        const payload = {
          consignee: {
            country: this.consigneeInformations.country
              ? this.consigneeInformations.country.value
              : null
          },
          packages: packagesWithPostType
        };

        if (this.shipmentInfo.incoterms) {
          const incotermsMap = { DDP: 1, DDU: 2, DAP: 3 };
          payload.incoterms_type = incotermsMap[this.shipmentInfo.incoterms] || undefined;
        }

        const response = await useService.getProviderPrices(payload);
        if (response && response.data && response.data.length) {
          // Sort providers by price ascending and select the first
          this.providers = response.data.sort((a, b) => a.total_price - b.total_price);
          this.selectedProviderId = this.providers[0].id;
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Error fetching prices'),
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        });
      } finally {
        this.isLoadingPrices = false;
      }
    },
    async handleComplete() {
      if (!this.selectedProviderId) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Please select a provider'),
            icon: 'AlertTriangleIcon',
            variant: 'warning'
          }
        });
        return;
      }

      this.isLoading = true;

      try {
        const uploadedDocumentId = this.documents.uploadedDocumentId;

        const packages = this.defaultPackageValues.map(pkg => ({
          weight: pkg.weight ? parseFloat(pkg.weight) : 0,
          width: pkg.width ? parseFloat(pkg.width) : 0,
          length: pkg.length ? parseFloat(pkg.length) : 0,
          height: pkg.height ? parseFloat(pkg.height) : 0,
          quantity: 1,
          items: pkg.items.map(item => ({
            description: item.description,
            quantity: parseInt(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            currency_code: item.currency || 'USD',
            origin: item.origin || 'TR',
            gtip: item.gtip || ''
          }))
        }));

        const payload = {
          consignor: {
            ...this.regionMapping({
              ...this.consignorInformations,
              city: this.consignorInformations.city || this.consignorInformations.state || '.'
            }),
            party_type: this.consignorInformations.party_type || 'business'
          },
          consignee: {
            ...this.regionMapping({
              ...this.consigneeInformations,
              city: this.consigneeInformations.city || this.consigneeInformations.state || '.'
            }),
            party_type: this.consigneeInformations.party_type || 'individual'
          },
          provider_id: this.selectedProviderId,
          preference: 0,
          packages: packages.map(pkg => ({
            ...pkg,
            package_type: 1,
            post_type: this.shipmentInfo.postType || 4
          })),
          reference_no: this.referenceNo || undefined,
          integration_id: this.order.integration_id || undefined,
          service_code: this.order.service_code || undefined,
          consignment_source: 'marketplace.coupang',
          invoice_number: this.documents.documentNumber || null,
          ioss_number: this.consignorInformations.ioss_number || null,
          address_resolver_type: 3,
          documents: uploadedDocumentId ? [{ id: uploadedDocumentId }] : [],
          generate_templated_documents: this.documents.autoProforma
            ? [{ type: 'PROFORMA_INVOICE' }]
            : []
        };

        if (this.shipmentInfo.incoterms) {
          const incotermsMap = { DDP: 1, DDU: 2, DAP: 3 };
          payload.incoterms_type = incotermsMap[this.shipmentInfo.incoterms] || undefined;
        }

        const response = await useService.createConsignment(payload);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Label Created Successfully!'),
            text: `${this.$t('Tracking Number')}: ${response.data.barcode || 'N/A'}`,
            icon: 'CheckIcon',
            variant: 'success'
          }
        });

        this.$emit('label-created');
        this.hide();
      } catch (error) {
        let msg = this.$t('Error creating label');
        if (error && typeof error === 'string') msg = error;
        if (error && error.message) msg = error.message;
        if (Array.isArray(error) && error.length && error[0].message) msg = error[0].message;

        this.showErrorMessage(msg, this.$t('Error creating label'));
      } finally {
        this.isLoading = false;
      }
    },
    getEstimatedDeliveryDate(deliveryTime) {
      const today = new Date();
      let minDays = 3;
      let maxDays = 5;

      if (deliveryTime) {
        const parts = String(deliveryTime).split('-');
        if (parts.length === 2) {
          minDays = parseInt(parts[0]) || 3;
          maxDays = parseInt(parts[1]) || 5;
        } else {
          const singleVal = parseInt(deliveryTime) || 3;
          minDays = singleVal;
          maxDays = singleVal + 2;
        }
      }

      const minDate = new Date(today);
      minDate.setDate(today.getDate() + minDays);

      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() + maxDays);

      const options = { day: 'numeric', month: 'short' };
      const locale = this.$i18n.locale || 'tr';

      const minStr = minDate.toLocaleDateString(locale, options);
      const maxStr = maxDate.toLocaleDateString(locale, options);

      return `${minStr} - ${maxStr}`;
    }
  }
};
</script>

<style>
@media (min-width: 992px) {
  .modal-1000 {
    max-width: 1000px !important;
  }
}

.offers-container {
  border-radius: 8px;
  overflow: hidden;
}

.offer-item {
  border: 2px solid #ebe9f1;
  transition: all 0.2s ease-in-out;
}

.offer-item:hover {
  background-color: #f8f8f8;
}

.offer-item-selected {
  border-color: #007bff !important;
  background-color: #f0f7ff;
}

.custom-radio-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #b4b7bd;
  background-color: #fff;
  flex-shrink: 0;
}

.offer-item-selected .custom-radio-circle {
  border-color: #007bff;
}

.custom-radio-inner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #007bff;
}

.selected-summary-box {
  background-color: #ebfaef !important;
  border: 1px solid #c2f0d1 !important;
  border-radius: 6px;
  color: #28c76f !important;
  font-size: 14px;
}

.create-label-btn {
  background-color: #28c76f !important;
  border-color: #28c76f !important;
  border-radius: 6px;
  font-size: 16px;
}

.create-label-btn:hover {
  background-color: #21a85b !important;
  border-color: #21a85b !important;
}

/* Hide next/finish button in wizard for the last step */
.is-last-step .stepbar-wrapper .vue-form-wizard .wizard-card-footer .wizard-footer-right {
  display: none !important;
}

/* Dark Mode Styles */
.dark-layout .offers-container {
  background-color: #161d31;
  border-color: #3b4253 !important;
}

.dark-layout .offers-container .card-header.bg-light {
  background-color: #283046 !important;
  border-bottom-color: #3b4253 !important;
}

.dark-layout .offers-container .card-header.bg-light span.text-muted {
  color: #b4b7bd !important;
}

.dark-layout .offers-container .card-header.bg-light span.font-weight-bold {
  color: #d0d2d6 !important;
}

.dark-layout .offer-item {
  border-color: #3b4253 !important;
  background-color: #283046;
}

.dark-layout .offer-item:hover {
  background-color: #343d55;
}

.dark-layout .offer-item-selected {
  border-color: #007bff !important;
  background-color: rgba(0, 123, 255, 0.1) !important;
}

.dark-layout .offer-item span.font-weight-bold.text-dark {
  color: #d0d2d6 !important;
}

.dark-layout .custom-radio-circle {
  background-color: #283046;
  border-color: #3b4253;
}

.dark-layout .offer-item-selected .custom-radio-circle {
  border-color: #007bff;
}

.dark-layout .selected-summary-box {
  background-color: rgba(40, 199, 111, 0.12) !important;
  border-color: rgba(40, 199, 111, 0.24) !important;
  color: #28c76f !important;
}

/* Fix modal header title alignment to push Sync button to the right */
#create-label-modal .modal-title {
  width: 100% !important;
}

/* Fix modal overflow/leakage caused by wizard progress masks */
.stepbar-wrapper .vue-form-wizard .wizard-nav-pills > li:first-child::before {
  left: 0 !important;
  width: 50% !important;
}

.stepbar-wrapper .vue-form-wizard .wizard-nav-pills > li:last-child::after {
  right: 0 !important;
  width: 50% !important;
}
</style>
